import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api-client';
import type { ListLinksResponse } from '@readlater/shared';

interface UseDeleteLinkOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  redirectTo?: string;
}

export function useDeleteLink(options?: UseDeleteLinkOptions) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (id: number) => {
      return api.delete(`/links/${id}`);
    },

    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['links'] });

      // Snapshot previous values
      const previousAll = queryClient.getQueryData<ListLinksResponse>(['links', 'all']);

      // Optimistically remove from all list queries
      const removeFromList = (key: string[]) => {
        queryClient.setQueryData<ListLinksResponse>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            links: old.links.filter((link) => link.id !== id),
          };
        });
      };

      removeFromList(['links', 'all']);
      removeFromList(['links', 'unread']);
      removeFromList(['links', 'favorites']);
      removeFromList(['links', 'archived']);

      return { previousAll };
    },

    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousAll) {
        queryClient.setQueryData(['links', 'all'], context.previousAll);
      }
      options?.onError?.(_err);
    },

    onSuccess: () => {
      options?.onSuccess?.();
      if (options?.redirectTo) {
        navigate(options.redirectTo);
      }
    },

    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['links'] });
    },
  });
}
